/**
 * "What a good one looks like" — one demonstration with its caption and its controls (SPEC_CAPTURE_UX_R2 §4).
 *
 * Two modes, one drawing:
 *   · MOTION — the ~2.5 s loop, with "Show again" (the §4 "Show me" replay, instrumented per §10);
 *   · STATIC — the three frames, advanced by a manual "Next", used under `prefers-reduced-motion` (the user asked
 *     for no motion) and in low-data mode (§7.4). Never an auto-looping animation in either case.
 *
 * The caption is the deck's verb phrase, never baked into the art — translated, and readable by the DEC-073 gate.
 */

import { useReducedMotion } from '@/capture/useReducedMotion';
import { useLowData } from '@/capture/lowData';
import { useT } from '@/i18n/runtime';
import { track } from '@/flow/instrument';
import { useCallback, useState } from 'react';
import { Icon } from '../components/Icon';
import { DemoArt } from './DemoArt';
import { type DemoType, demoCaptionKey } from './demoTypes';

export const DEMO_FRAMES = 3;

export function ShowMe({ type, where = 'sheet' }: { type: DemoType; where?: string }): JSX.Element {
  const { t } = useT();
  const reduced = useReducedMotion();
  const lowData = useLowData();
  const staticOnly = reduced || lowData.on;
  const [frame, setFrame] = useState(0);
  /** Remount key — the honest way to restart a SMIL loop from its first pose. */
  const [run, setRun] = useState(0);

  const next = useCallback(() => setFrame((f) => (f + 1) % DEMO_FRAMES), []);
  const again = useCallback(() => {
    setRun((r) => r + 1);
    setFrame(0);
    track('showme.replay', { type, where, mode: staticOnly ? 'frames' : 'motion' });
  }, [type, where, staticOnly]);

  return (
    <div className="sv-showme" data-testid="show-me" data-showme={type} data-mode={staticOnly ? 'frames' : 'motion'}>
      <div className="sv-showme-stage">
        <DemoArt key={`${type}-${run}`} type={type} frame={staticOnly ? frame : null} />
      </div>
      <div className="sv-showme-cap" data-testid="show-me-caption">
        {t(demoCaptionKey(type))}
      </div>
      {staticOnly ? (
        <div className="sv-showme-frames">
          <span className="sv-showme-dots" aria-hidden="true" data-testid="show-me-dots">
            {Array.from({ length: DEMO_FRAMES }, (_, i) => (
              <i key={`f${i}`} className={i === frame ? 'is-on' : ''} />
            ))}
          </span>
          <button type="button" className="sv-showme-btn" onClick={next} data-testid="show-me-next">
            {t('sv.showme.next')} <Icon name="chevron" size={14} className="rot" />
          </button>
        </div>
      ) : (
        <button type="button" className="sv-showme-btn" onClick={again} data-testid="show-me-again">
          <Icon name="redo" size={14} /> {t('sv.showme.again')}
        </button>
      )}
    </div>
  );
}
